/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useReducer, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {defineMessage, FormattedMessage, useIntl} from 'react-intl'
import {
    Box,
    Button,
    Flex,
    Stack,
    Text,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import FormActionButtons from '../../../components/forms/form-action-buttons'
import {MESSAGE_PROPTYPE} from '@salesforce/retail-react-app/app/utils/locale'
import AddressFieldsWithAutocomplete from '../../../components/autocomplete-address-fields'
import AccountAddressFieldsWithAutocomplete from '../../../components/autocomplete-address-fields/account-address-fields'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {remCalc} from '../../../theme/_utils'
import {basic_gtm_event} from '../../../custom-api/layers/default'
import {useGoogleTagManager} from '../../../custom-api/hooks/use-google-tag-manager'
import {callLoqateBatchService, defaultLoqateObjectValidate} from '../../../services/loqate'
import getCountryISO3 from 'country-iso-2-to-3'
import {Link} from 'react-router-dom'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useLocaleCountry} from '../../../hooks/use-locale-custom'
import {useCountryLabels} from '../../../components/autocomplete-address-fields/use-country-labels'
import {AddressCheckModal} from './address-check-modal'
import {useGlobalCustomPreference} from '../../../hooks/use-custom-preference'

const saveButtonMessage = defineMessage({
    defaultMessage: 'Save',
    id: 'shipping_address_edit_form.button.save',
})

const createAddressObjectToValidate = (values) => {
    return {
        Country: getCountryISO3(values.countryCode?.value ?? values.countryCode),
        Address1: values.address1,
        Line1: values.address1,
        Line2: values.address2,
        CountryIso2: values.countryCode?.value ?? values.countryCode,
        City: values.city,
        Locality: values.city,
        PostalCode: values.postalCode,
        ProvinceCode: values.stateCode?.value ?? values.stateCode,
        AdministrativeArea: values.stateCode?.value ?? values.stateCode,
    }
}

export const AddressEditForm = ({
    hasSavedAddresses,
    form,
    formTitleAriaLabel,
    isBillingAddress = false,
    onClose,
    submitForm,
    selectedAddressId,
    addressType,
    hideSubmitButton,
    action,
    isAccountEdit = false,
    variant,
    isInline = false,
    addressesOptions = [],
    setAddressesOptions,
    checkAddressIsOpen,
    closeCheckAddressModal,
}) => {
    const {formatMessage} = useIntl()
    const {data: customer} = useCurrentCustomer()
    const {addLayer} = useGoogleTagManager()
    const {customParams} = getConfig()
    const {loqateApiKey} = customParams
    const [autocompleteObj, setAutocompleteObj] = useState(null)
    const regionsPref = useGlobalCustomPreference('regionConfig')
    const originalFormDataRef = useRef(null)

    if (originalFormDataRef.current === null) {
        originalFormDataRef.current = form.getValues()
    }

    const originalFormData = originalFormDataRef.current

    const isHidden = addressesOptions?.length > 0 && isInline

    let title

    if (isBillingAddress) {
        title = selectedAddressId
            ? formatMessage({
                  defaultMessage: 'Edit billing Address',
                  id: 'shipping_address_selection.title.edit_billing',
              })
            : formatMessage({
                  defaultMessage: 'Add new billing Address',
                  id: 'shipping_address_selection.title.add_new_billing',
              })
    } else {
        title = selectedAddressId
            ? formatMessage({
                  defaultMessage: 'Edit Shipping Address',
                  id: 'shipping_address_selection.title.edit_shipping',
              })
            : formatMessage({
                  defaultMessage: 'Add new shipping Address',
                  id: 'shipping_address_selection.title.add_new_shipping',
              })
    }

    const styles = useMultiStyleConfig('AddressDisplay', {variant})

    const localeCountry = useLocaleCountry()
    const {getEuropeanCountries} = useCountryLabels()
    const isCurrentCountryEuropean = getEuropeanCountries().some((c) => c.value === localeCountry)

    const clearAll = () => {
        const values = form.getValues()
        Object.keys(values).forEach((key) => {
            if (key !== 'addressId' && key !== 'countryCode') {
                form.setValue(key, '')
            }
        })

        // reset only if `isBillingAddress` is true thus the country code is not disabled
        if (isBillingAddress && isCurrentCountryEuropean) {
            form.setValue('countryCode', '')
        }
    }

    const {locale} = useMultiSite()

    //TODO: When we have id, add english privacy policy fallback
    const privacyPolicy = locale?.privacyPolicy || ''

    const [state, dispatch] = useReducer(
        (state, action) => {
            switch (action.type) {
                case 'SET_PREVENT_SUBMIT':
                    return {...state, preventSubmit: action.payload}
                default:
                    return state
            }
        },
        {
            preventSubmit: true,
        },
    )

    const handlePreventSubmit = (set) => {
        dispatch({type: 'SET_PREVENT_SUBMIT', payload: set})
    }

    const checkIfAddressWasModified = (formData) => {
        let hasChanged = false

        if (!autocompleteObj) {
            hasChanged =
                normalize(formData.address1) !== normalize(originalFormData.address1) ||
                normalize(formData.addressNumber) !== normalize(originalFormData.addressNumber) ||
                normalize(formData.city) !== normalize(originalFormData.city) ||
                normalize(formData.postalCode)?.replaceAll(' ', '') !==
                    normalize(originalFormData.postalCode)?.replaceAll(' ', '') ||
                normalize(formData.stateCode?.value) !==
                    normalize(originalFormData.stateCode?.value || originalFormData.stateCode) ||
                (originalFormData.stateCode?.label &&
                    normalize(formData.stateCode?.label) !==
                        normalize(originalFormData.stateCode?.label))
        } else {
            // Checks if the user has manually altered the address
            // after selecting a suggestion from the Loqate autocomplete.
            hasChanged =
                (formData.address1 !== autocompleteObj.Street &&
                    formData.address1 !== autocompleteObj.Line1) ||
                (formData?.addressNumber &&
                    formData.addressNumber !== autocompleteObj.BuildingNumber) ||
                formData.city !== autocompleteObj.City ||
                formData.postalCode?.replaceAll(' ', '') !==
                    autocompleteObj.PostalCode?.replaceAll(' ', '') ||
                (formData.stateCode?.value !== autocompleteObj.ProvinceCode &&
                    !normalize(formData.stateCode?.label).includes(
                        normalize(autocompleteObj.ProvinceName),
                    ))
        }

        return hasChanged
    }

    const getProvinceDataFromCustomPref = (administrativeArea) => {
        if (!regionsPref) return null
        const regionList =
            regionsPref?.[
                form?.getValues()?.countryCode?.value || form?.getValues()?.countryCode
            ] || []
        const matchedRegion = regionList.find(
            (r) =>
                r?.ISOCode?.toUpperCase().includes(administrativeArea.toUpperCase()) ||
                (administrativeArea.length > 3 &&
                    r?.Name?.toUpperCase().includes(administrativeArea.toUpperCase())),
        )

        return matchedRegion
    }

    // Constructs a standardized "suggestion" object from the Loqate Batch API response.
    // It's used for both manual entries and autocomplete-driven entries to create a consistent
    // object for comparison and display in the address validation modal.
    const buildSuggestionFromBatchMatch = (match, form) => {
        if (!match) return null
        const data = getProvinceDataFromCustomPref(match.AdministrativeArea) || {}
        let suggestionAddress = match.DeliveryAddress1 || match.Address1
        if (match.Thoroughfare && (match.PremiseNumber || match.BuildingNumber)) {
            suggestionAddress = `${match.Thoroughfare} ${
                match.PremiseNumber || match.BuildingNumber
            }`
        }
        return {
            Line1: suggestionAddress,
            // For autocomplete flows, we prioritize the state/province label from the form state
            // because the batch API response only contains the code. For manual flows, we use the code.
            ProvinceName: data?.Name || form.stateCode?.value || form.stateCode?.label || '',
            PostalCode: match.PostalCode || form.postalCode,
            CountryIso2: match.Country || form.countryCode?.value || form.countryCode,
            City: match.Locality || form.city,
            ProvinceCode: data?.ISOCode || form.stateCode?.value || '',
            isManual: true,
        }
    }

    const buildSuggestionFromAddressLookup = (addressLookup) => {
        if (!addressLookup) return null
        const provinceData = addressLookup?.ProvinceCode || addressLookup?.ProvinceName
        const data = getProvinceDataFromCustomPref(provinceData)
        return {
            ...addressLookup,
            ProvinceName: data?.Name || addressLookup.ProvinceName || '',
            ProvinceCode: data?.ISOCode || addressLookup.ProvinceCode || '',
        }
    }

    const normalize = (val) => (val || '').toString().trim().toLowerCase()

    // Compares the user's form data against the Loqate-generated suggestion.
    // It returns true if any significant part of the address is different, triggering the modal.
    // The comparison is normalized (lowercase, trimmed) to avoid false positives.
    const hasMismatchWithSuggestion = (suggestion, formData) => {
        if (!suggestion) return false

        const diff =
            normalize(formData.address1) !== normalize(suggestion.Line1) ||
            normalize(formData.city) !== normalize(suggestion.City) ||
            normalize(formData.postalCode) !== normalize(suggestion.PostalCode) ||
            (!normalize(formData.stateCode?.value).includes(normalize(suggestion.ProvinceName)) &&
                !normalize(formData.stateCode?.label).includes(
                    normalize(suggestion.ProvinceName),
                )) ||
            normalize(formData.countryCode?.value || formData.countryCode) !==
                normalize(suggestion.CountryIso2)
        return diff
    }

    const onSubmitWithPrecheck = async (form) => {
        let shouldProceed = false
        const isAddressChanged = checkIfAddressWasModified(form)
        // Validation should run if first attempt AND (manual entry OR user changed an autocomplete selection)
        const shouldRunValidation =
            state.preventSubmit && ((!autocompleteObj && !selectedAddressId) || isAddressChanged)
        if (shouldRunValidation) {
            const response = await callLoqateBatchService(
                defaultLoqateObjectValidate(loqateApiKey, createAddressObjectToValidate(form)),
            )

            const match = response?.[0]?.Matches?.[0]
            const suggestion = autocompleteObj
                ? buildSuggestionFromAddressLookup(form?.addressLookup)
                : buildSuggestionFromBatchMatch(match, form)

            const mismatched = hasMismatchWithSuggestion(suggestion, form)
            if (isAddressChanged && mismatched) {
                // If a mismatch is found, we prepare to show the validation modal.
                // We pass both the Loqate suggestion and the user's original input to the modal.
                // The 'isManual' flag is attached to the user's input object for consistent handling in the modal.
                const userInputWithFlag = {
                    ...form,
                    isManual: !autocompleteObj,
                }
                setAddressesOptions([suggestion, userInputWithFlag].filter(Boolean))
            } else {
                // If there's no mismatch, the address is considered valid, and we can proceed with form submission.
                shouldProceed = true
            }

            // We set `preventSubmit` to false to avoid re-running validation if the user
            // confirms their address in the modal, which re-triggers this submission handler.
            handlePreventSubmit(false)
        } else {
            // If validation isn't needed (e.g., second submission from modal), proceed directly.
            shouldProceed = true
        }

        if (shouldProceed) {
            await submitForm(form)
        }
    }

    const onError = async () => {
        const {errors} = form.formState
        if (errors) {
            await addLayer(
                basic_gtm_event.bind(this, {
                    event: 'fillInErrors',
                    eventCategory: 'ecommerce',
                    eventAction: 'checkout',
                    eventLabel: 'fillInErrors',
                }),
            )
        }
    }

    return (
        <Box
            {...(hasSavedAddresses &&
                !isBillingAddress && {
                    gridColumn: [1, 1, 'span 2'],
                })}
            {...styles.formContainer}
            data-testid="sf-shipping-address-edit-form"
        >
            {addressesOptions?.length > 0 && (
                <AddressCheckModal
                    isOpen={checkAddressIsOpen}
                    closeModal={closeCheckAddressModal}
                    addressesOptions={addressesOptions}
                    form={form}
                    submitForm={submitForm}
                    isInline={isInline}
                    isManual={!autocompleteObj}
                />
            )}
            <Stack
                gap="0"
                style={{
                    visibility: isHidden ? 'hidden' : 'visible',
                    height: isHidden ? 0 : 'auto',
                }}
            >
                {!customer.isGuest && <Text {...styles.formTitle}>{title}</Text>}
                <Box
                    {...(!customer.isGuest &&
                        !isAccountEdit && {
                            maxHeight: {base: remCalc(328), md: 'initial'},
                            overflowY: {base: 'scroll', md: 'initial'},
                            overflowX: 'hidden',
                            paddingRight: {base: remCalc(8), md: remCalc(0)},
                            sx: {
                                '&::-webkit-scrollbar': {
                                    width: remCalc(6),
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    background: 'button.border.inverse-primary-focused',
                                    borderRadius: remCalc(6.5),
                                },
                                '&::-webkit-scrollbar-track': {
                                    background: 'background.light',
                                },
                            },
                        })}
                >
                    <Flex {...styles.clearFieldsContainer}>
                        <Button
                            variant="text-button"
                            {...{
                                textStyle: 'body-small',
                                fontWeight: 'normal',
                            }}
                            onClick={() => clearAll()}
                        >
                            <FormattedMessage
                                defaultMessage="Clear all fields"
                                id="address_form.clear_all"
                            />
                        </Button>
                        <Text
                            variant="text-button"
                            {...{
                                textStyle: 'body-small',
                                color: 'text.secondary',
                            }}
                        >
                            <FormattedMessage
                                defaultMessage="*Mandatory fields"
                                id="address_form.mandatory_fields"
                            />
                        </Text>
                    </Flex>

                    <Box {...styles.addressFieldsContainer}>
                        {isAccountEdit && isBillingAddress && addressType === 'private' ? (
                            <AccountAddressFieldsWithAutocomplete
                                form={form}
                                formTitleAriaLabel={formTitleAriaLabel}
                                action={action}
                                setAutocompleteObj={setAutocompleteObj}
                                isBillingAddress={isBillingAddress}
                            />
                        ) : (
                            <AddressFieldsWithAutocomplete
                                form={form}
                                formTitleAriaLabel={formTitleAriaLabel}
                                isBillingAddress={isBillingAddress}
                                addressType={addressType}
                                action={action}
                                variant={isAccountEdit ? 'accountForm' : null}
                                setAutocompleteObj={setAutocompleteObj}
                            />
                        )}
                    </Box>

                    <Text {...styles.privacyNoteText}>
                        <FormattedMessage
                            defaultMessage="We want to keep your privacy safe. We will call you only for important communications regarding your order. Consult our "
                            id="address_form.privacy_policy_first_text"
                        />{' '}
                        <Link to={`/${locale?.alias}${privacyPolicy}`}>
                            <Text as="span" textDecor="underline" display="inline">
                                <FormattedMessage
                                    id="address_form.privacy_policy_link"
                                    defaultMessage="Privacy Policy"
                                />{' '}
                            </Text>
                        </Link>
                        <FormattedMessage
                            id="address_form.privacy_policy_second_text"
                            defaultMessage=" for further information."
                        />
                    </Text>
                </Box>

                {!hideSubmitButton && (
                    <FormActionButtons
                        cancelButtonProps={customer.isGuest ? {display: 'none'} : {}}
                        saveButtonProps={
                            customer.isGuest
                                ? {width: {base: '100%', md: '50%', xl: remCalc(332)}}
                                : {}
                        }
                        saveButtonLabel={saveButtonMessage}
                        onCancel={() => {
                            onClose()
                        }}
                        onSubmit={async () =>
                            await form.handleSubmit(onSubmitWithPrecheck, onError)()
                        }
                        variant="address-buttons"
                    />
                )}
            </Stack>
        </Box>
    )
}

AddressEditForm.propTypes = {
    title: PropTypes.string,
    hasSavedAddresses: PropTypes.bool,
    hideSubmitButton: PropTypes.bool,
    form: PropTypes.object,
    formTitleAriaLabel: MESSAGE_PROPTYPE,
    isBillingAddress: PropTypes.bool,
    onClose: PropTypes.func,
    submitForm: PropTypes.func,
    selectedAddressId: PropTypes.string,
    addressType: PropTypes.string,
    action: PropTypes.func,
    isAccountEdit: PropTypes.bool,
    variant: PropTypes.string,
    isInline: PropTypes.bool,
    addressesOptions: PropTypes.array,
    setAddressesOptions: PropTypes.func,
    checkAddressIsOpen: PropTypes.bool,
    closeCheckAddressModal: PropTypes.func,
}
